import {
  LegalProtectionEligibilityResponse,
  LegalProtectionEligibilityResponseStatusEnum
} from "api/risk";

export type EligibilityVariant = "eligible" | "toComplete" | "nonEligible" | "notApplicable";

export type EligibilityFigureKey = "totalRevenue" | "revenueCeiling" | "totalEmployees" | "subsidiariesCount";

export interface EligibilityFigure {
  key: EligibilityFigureKey;
  value: number;
}

export interface EligibilityView {
  variant: EligibilityVariant;
  /** The pricing step is reachable. */
  canContinue: boolean;
  /** Reasons as sent by the back, never rewritten. */
  reasons: string[];
  /** Figures sent by the back, in display order; missing ones are left out. */
  figures: EligibilityFigure[];
}

const FIGURE_KEYS: EligibilityFigureKey[] = ["totalRevenue", "revenueCeiling", "totalEmployees", "subsidiariesCount"];

const toVariant = (status: LegalProtectionEligibilityResponseStatusEnum): EligibilityVariant => {
  switch (status) {
    case LegalProtectionEligibilityResponseStatusEnum.Eligible:
      return "eligible";
    case LegalProtectionEligibilityResponseStatusEnum.NonEligible:
      return "nonEligible";
    default:
      return "toComplete";
  }
};

/**
 * Display of the legal protection eligibility response. Nothing is computed locally:
 * the verdict, the reasons and the figures are the back's.
 */
export const getEligibilityView = (response: LegalProtectionEligibilityResponse): EligibilityView => {
  const figures = FIGURE_KEYS.flatMap(key => {
    const value = response[key];
    return value == null ? [] : [{ key, value }];
  });

  // The rule does not apply (natural person subscriber): no verdict, the pricing stays open.
  if (!response.isApplicable) {
    return { variant: "notApplicable", canContinue: true, reasons: [], figures };
  }

  const variant = toVariant(response.status);

  return {
    variant,
    canContinue: variant === "eligible",
    reasons: response.reasons ?? [],
    figures
  };
};
